<template>
	<l-autocomplete
		v-model="timeText"
		@update:search-input="setTempInput"
		:items="items"
		:rules="validationRules"
		v-bind="$attrs"
		v-on="$listeners"
		ref="autocomplete"
	/>
</template>
<script>
import dayjs from 'dayjs';
import LAutocomplete from '../LAutocomplete.vue';

import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

export default {
	components: { LAutocomplete },
	name: 'SimpleTimecomplete',
	props: {
		dayjsObject: {
			type: Object,
			required: false,
			validator(value) {
				if (value instanceof dayjs) return true;
				else return `Prop "dayjsObject" must be an instanceof "dayjs".`;
			},
		},
		dateObject: {
			type: Date,
			required: false,
		},
		timeString: {
			type: String,
			required: false,
		},
		minutes: {
			type: Number,
			required: false,
		},
		minuteStep: {
			type: Number,
			default: 30,
			validator(value) {
				if (Number.isInteger(value) && value >= 1 && value <= 60) return true;
				else return `Prop "minuteStep" must be a number >=1 and <=60.`;
			},
		},
		minTime: {
			type: [Number, String, Date, dayjs],
			required: false,
		},
		maxTime: {
			type: [Number, String, Date, dayjs],
			required: false,
		},
	},
	data() {
		return {
			timeText: null,
		};
	},
	computed: {
		items() {
			const out = [];
			for (const hour of [...Array(24).keys()]) {
				const hourString = ('0' + hour).slice(-2);
				for (const n of [...Array(60 / this.minuteStep).keys()]) {
					const minute = n * this.minuteStep;
					const minuteString = ('0' + minute).slice(-2);

					const timeInMinutes = hour * 60 + minute;
					const minTimeInMinutes = this.$_.defaultTo(this.anyTimeToMinutes(this.minTime), 0);
					const maxTimeInMinutes = this.$_.defaultTo(this.anyTimeToMinutes(this.maxTime), 24 * 60 - this.minuteStep);

					if (timeInMinutes >= minTimeInMinutes && timeInMinutes <= maxTimeInMinutes)
						out.push(`${hourString}:${minuteString}`);
				}
			}
			return out;
		},
		validationRules() {
			const attrsRules = this.$_.get(this, '$attrs.rules', []);
			return [this.isTimeValid(this.timeText) || 'Invalid Time', ...attrsRules];
		},
	},
	methods: {
		anyTimeToMinutes(time) {
			if (typeof time === 'number') return time;
			if (time instanceof Date) {
				time = dayjs(time);
			}
			if (time instanceof dayjs) {
				time = time.format('HH:mm');
			}
			if (typeof time === 'string') {
				if (!time.includes(':')) throw new Error(`Unknown time format, missing ":" in time "${time}"`);
				let [hours, minutes] = time.split(':');
				hours = +hours;
				minutes = +minutes;
				return hours * 60 + minutes;
			}
		},
		checkMinutesValid(minutes) {
			return minutes % this.minuteStep === 0;
		},
		setTempInput(value) {
			if (typeof value === 'string') {
				const lastChar = value.slice(-1);
				if (value.length === 3 && lastChar !== ':') {
					value = value.slice(0, 2) + ':' + lastChar;
				}
			}

			this.$refs.autocomplete.$children[0].lazySearch = value;
		},
		isTimeValid(value) {
			return /^((0[0-9])|(1[0-9])|(2[0-3])):[0-5][0-9]$/.test(value);
		},
	},
	watch: {
		timeString: {
			immediate: true,
			handler: function () {
				if (this.timeString && this.isTimeValid(this.timeString)) this.timeText = this.timeString;
			},
		},
		dateObject: {
			immediate: true,
			handler: function () {
				if (this.dateObject) this.timeText = dayjs(this.dateObject).format('HH:mm');
			},
		},
		minutes: {
			immediate: true,
			handler: function () {
				if (this.minutes) {
					let minutes = this.minutes;
					const hours = Math.floor(minutes / 60);
					minutes = minutes % 60;

					this.timeText = ('0' + hours).slice(-2) + ':' + ('0' + minutes).slice(-2);
				}
			},
		},
		timeText: {
			handler: function () {
				if (!this.timeText) return;
				if (typeof this.timeText === 'string' && this.isTimeValid(this.timeText)) {
					const hour = +this.timeText.slice(0, 2);
					const minute = +this.timeText.slice(3, 5);
					if (this.dayjsObject) {
						let cloneDayjsObject = dayjs(this.dayjsObject);
						cloneDayjsObject = cloneDayjsObject.hour(hour);
						cloneDayjsObject = cloneDayjsObject.minute(minute);
						this.$emit('update:dayjsObject', cloneDayjsObject);
					}
					if (this.dateObject) {
						const cloneDateObject = new Date(this.dateObject);
						cloneDateObject.setHours(hour);
						cloneDateObject.setMinutes(minute);
						this.$emit('update:dateObject', cloneDateObject);
					}
					this.$emit('update:minutes', hour * 60 + minute);
					this.$emit('update:timeString', this.timeText);
				}
			},
		},
	},
};
</script>
